"use client";

import React from "react";

export interface PriceRange {
  label: string;
  situation: string;
  description: string;
}

export interface PricingSelectorProps {
  title: string;
  subtitle?: string;
  therapistTypes: {
    type: 'resident' | 'associate';
    label: string;
    description: string;
    priceRanges: PriceRange[];
  }[];
  selectedTherapistType: 'resident' | 'associate';
  onTherapistTypeChange: (type: 'resident' | 'associate') => void;
  selectedPriceRange: number;
  onPriceRangeChange: (index: number) => void;
  bgColor?: string;
}

export function PricingSelector({
  title,
  subtitle,
  therapistTypes,
  selectedTherapistType,
  onTherapistTypeChange,
  selectedPriceRange,
  onPriceRangeChange,
  bgColor = "bg-secondary-50"
}: PricingSelectorProps) {
  const currentTherapistType = therapistTypes.find(t => t.type === selectedTherapistType);
  const priceRanges = currentTherapistType?.priceRanges || [];

  return (
    <section className={`py-16 md:py-20 ${bgColor}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {/* Therapist Type Toggle */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-white rounded-full p-1 shadow-sm">
            <div className="grid grid-cols-2 gap-1">
              {therapistTypes.map((therapistType) => (
                <button
                  key={therapistType.type}
                  onClick={() => onTherapistTypeChange(therapistType.type)}
                  className={`
                    px-6 py-3 rounded-full transition-all duration-200 font-medium
                    ${selectedTherapistType === therapistType.type
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {therapistType.label}
                </button>
              ))}
            </div>
          </div>
          {currentTherapistType && (
            <p className="text-center text-gray-600 mt-4">
              {currentTherapistType.description}
            </p>
          )}
        </div>

        {/* Price Range Slider */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="mb-8">
              <input
                type="range"
                min="0"
                max={priceRanges.length - 1}
                step="1"
                value={selectedPriceRange}
                onChange={(e) => onPriceRangeChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(selectedPriceRange / (priceRanges.length - 1)) * 100}%, #E5E7EB ${(selectedPriceRange / (priceRanges.length - 1)) * 100}%, #E5E7EB 100%)`
                }}
              />
              <div className="flex justify-between mt-2">
                {priceRanges.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index <= selectedPriceRange ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Selected Price Info */}
            {priceRanges[selectedPriceRange] && (
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {priceRanges[selectedPriceRange].label}
                </div>
                <div className="text-lg text-gray-700 mb-2">
                  {priceRanges[selectedPriceRange].situation}
                </div>
                <div className="text-gray-600">
                  {priceRanges[selectedPriceRange].description}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}